import {
  MIN_NAME_TOKEN_LENGTH,
  MIN_ORG_NUMBER_DIGITS,
  MIN_PERSON_NAME_LENGTH,
} from "@/v2-backoffice-client/crm/crm.constants"

/** Digits only — the organisation-number normalisation the backend matches on. */
export function digitsOnly(value: string) {
  return value.replace(/\D/g, "")
}

/** Whether an org.nr is complete enough for an exact lookup. */
export function isSearchableOrgNumber(value: string | undefined) {
  return digitsOnly(value ?? "").length >= MIN_ORG_NUMBER_DIGITS
}

/**
 * Whether a typed company name has a word worth searching: the backend
 * drops legal forms (AS, ASA, …) and short fragments, so mirror the minimum
 * here — a lone "AS" or "A" never fires a request.
 */
export function hasSearchableNameToken(name: string | undefined) {
  return (name ?? "")
    .split(/[\s\-+&/,.()"']+/)
    .some((word) => word.length >= MIN_NAME_TOKEN_LENGTH)
}

/** Whether a website carries a host with a dot (a searchable domain). */
export function hasSearchableDomain(website: string | undefined) {
  const value = (website ?? "").trim().toLowerCase()
  if (!value) return false
  const host = value
    .replace(/^[a-z]+:\/\//, "")
    .replace(/^www\./, "")
    .split(/[/?#:]/)[0]
  return host.includes(".") && !host.includes("@") && !host.endsWith(".")
}

/** Whether both name parts are long enough for a name lookup. */
export function isSearchablePersonName(
  firstName: string | undefined,
  lastName: string | undefined,
) {
  return (
    (firstName ?? "").trim().length >= MIN_PERSON_NAME_LENGTH &&
    (lastName ?? "").trim().length >= MIN_PERSON_NAME_LENGTH
  )
}

/** Whether a typed email is complete enough for an exact lookup. */
export function isCompleteEmail(value: string | undefined) {
  const email = (value ?? "").trim()
  const at = email.indexOf("@")
  if (at <= 0 || at === email.length - 1) return false
  const domain = email.slice(at + 1)
  return domain.includes(".") && !domain.endsWith(".") && !email.includes(" ")
}

/** Lower + trim — the email normalisation the backend links on. */
export function normalizeEmail(value: string | undefined) {
  return (value ?? "").trim().toLowerCase()
}
